import { useNavigate, Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import HamburgerMenuLinear from "~icons/solar/hamburger-menu-linear";
import AltArrowDownLinear from "~icons/solar/alt-arrow-down-linear";
import SettingsLinear from "~icons/solar/settings-linear";
import Logout2Linear from "~icons/solar/logout-2-linear";
import { useAuthStore } from "../../stores/auth-store";
import { logout } from "../../lib/auth-service";
import { NotificationBell } from "../notifications/NotificationBell";
import { Avatar } from "../ui/Avatar";
import { IconButton } from "../ui/IconButton";
import { Dropdown, DropdownItem } from "../ui/Dropdown";
import dahlingLogo from "../../assets/dahling_logo_1.svg";
import { cn } from "../../lib/utils";

interface TopBarProps {
  onMobileMenuOpen: () => void;
}

/** Glass header: logo, notifications, user menu. */
export function TopBar({ onMobileMenuOpen }: TopBarProps) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const firstName = user?.first_name ?? "";
  const lastName = user?.last_name ?? "";
  const displayName =
    firstName || lastName ? `${firstName} ${lastName}`.trim() : "Unknown User";
  const email = user?.email ?? "";

  function handleSignOut() {
    logout();
    navigate("/login", { replace: true });
    queryClient.clear();
  }

  return (
    <header className="sticky top-0 z-40 border-b border-black/[0.05] bg-white/90 backdrop-blur-glass">
      <div className="mx-auto flex h-14 w-full max-w-[1512px] items-center justify-between px-3 sm:px-5 lg:px-6">
        {/* Left: mobile menu + logo */}
        <div className="flex items-center gap-3">
          <IconButton
            icon={HamburgerMenuLinear}
            aria-label="Open navigation menu"
            variant="secondary"
            className="rounded-xl lg:hidden"
            onClick={onMobileMenuOpen}
          />
          <Link
            to="/"
            aria-label="Dahling home"
            className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <img src={dahlingLogo} alt="Dahling" className="h-6 w-auto" />
          </Link>
        </div>

        {/* Right: notifications + user */}
        <div className="flex items-center gap-2.5 sm:gap-3">
          <NotificationBell />

          <Dropdown
            align="end"
            minWidth={240}
            trigger={({ toggle, open, ...a }) => (
              <button
                type="button"
                onClick={toggle}
                aria-label={`User menu for ${displayName}`}
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 rounded-2xl border px-2 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                  open
                    ? "border-black/15 bg-black/5"
                    : "border-transparent hover:bg-black/5"
                )}
                {...a}
              >
                <Avatar
                  name={displayName}
                  src={user?.avatar_url}
                  size="sm"
                  className="size-[34px] shadow-card ring-2 ring-white"
                />
                <div className="hidden text-left sm:block">
                  <p className="whitespace-nowrap text-xs font-bold leading-tight text-ink">
                    {displayName}
                  </p>
                  <p className="whitespace-nowrap text-xs leading-tight text-ink-muted">
                    {email}
                  </p>
                </div>
                <AltArrowDownLinear
                  aria-hidden="true"
                  className={cn(
                    "size-3.5 text-ink-faint transition-transform duration-200",
                    open && "rotate-180 text-ink"
                  )}
                />
              </button>
            )}
          >
            <div className="mb-1 flex items-center gap-2.5 border-b border-black/[0.06] p-2.5">
              <Avatar name={displayName} src={user?.avatar_url} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-ink">{displayName}</p>
                <p className="truncate text-[11px] text-ink-muted">{email}</p>
              </div>
            </div>
            <DropdownItem
              icon={SettingsLinear}
              onSelect={() => navigate("/settings/profile")}
            >
              Settings
            </DropdownItem>
            <DropdownItem icon={Logout2Linear} danger onSelect={handleSignOut}>
              Sign out
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
    </header>
  );
}
